import classNames from "classnames";
import Button from "../Button/Button";
import styles from "./FileUpload.module.scss";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useToast } from "../Toast/ToastContext";
import { zodResolver } from "@hookform/resolvers/zod";

const schema = z.object({
  image: z.instanceof(File),
});

const uploadSchema = z.object({
  id: z.string(),
  url: z.string(),
  width: z.number(),
  height: z.number(),
});

type FormData = z.infer<typeof schema>;

type UploadData = z.infer<typeof uploadSchema>;

function FileUpload({
  className,
  onUpload,
}: {
  className?: string;
  onUpload: (image: UploadData) => Promise<void>;
}) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });
  const { showToast } = useToast();

  const onSubmit = async (data: FormData) => {
    if (!data.image) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", data.image);

      const response = await fetch(
        `${import.meta.env.VITE_IMAGE_UPLOAD_HOST}/upload`,
        {
          method: "POST",
          body: formData,
        },
      );

      const json: unknown = await response.json();
      const parsedJson = uploadSchema.safeParse(json);

      if (!parsedJson.success) {
        throw new Error("Invalid response from server");
      }

      await onUpload(parsedJson.data);
    } catch {
      showToast({
        title: "Failed to upload image",
        variant: "error",
      });
    }

    reset();
  };

  return (
    <form className={classNames(styles.label, className)}>
      <Controller
        control={control}
        name="image"
        render={({ field }) => (
          <label
            htmlFor={field.name}
            className={classNames(styles.label, className)}
          >
            <Button
              variant="primary"
              className={styles.button}
              asDiv
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Upload image
            </Button>
            <input
              {...field}
              value=""
              onChange={(e) => {
                field.onChange(e.target.files?.[0]);
                void handleSubmit(onSubmit)();
              }}
              id={field.name}
              type="file"
              accept="image/*"
              className={styles.input}
            />
          </label>
        )}
      />
    </form>
  );
}

export default FileUpload;
